import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { SessionResults } from '@floorpoll/shared';
import { ExportResults } from '../../components/ExportResults.js';
import { FormError } from '../../components/Form.js';
import { ResultBars } from '../../components/ResultBars.js';
import { formatRunDate } from '../../components/RunHistory.js';
import { ApiError } from '../../lib/api.js';
import { quizApi } from '../../lib/quizApi.js';
import { track } from '../../lib/track.js';

/**
 * Story #46 — one presentation run, as it stood the night it ended.
 *
 * Addressed by run id with no poll in the path, and it links back to the poll
 * rather than being reached through it: a run outlives the poll it came from,
 * so this page has to work when there is nothing left to go back to.
 */
export function RunResultsPage() {
  const { sessionId = '' } = useParams();
  const [results, setResults] = useState<SessionResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setResults(await quizApi.sessionResults(sessionId));
      track('runOpened');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load that run.');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <p className="muted">Loading…</p>;
  if (!results) {
    return (
      <section>
        <FormError message={error ?? 'Run not found.'} />
        <Link to="/polls">Back to polls</Link>
      </section>
    );
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <Link to={`/polls/${results.quizId}/results`} className="muted">
            ← All results
          </Link>
          <h1>{results.title}</h1>
          <p className="muted">
            {formatRunDate(results.endedAt)} · {results.participantCount}{' '}
            {results.participantCount === 1 ? 'person' : 'people'} · {results.totalVotes}{' '}
            {results.totalVotes === 1 ? 'vote' : 'votes'}
          </p>
        </div>
        <div className="page-head-actions">
          <ExportResults
            source={{
              title: results.title,
              runDate: results.endedAt,
              screens: results.screens,
            }}
          />
        </div>
      </div>

      <p className="form-notice" role="note">
        This is one run, as it was on the night. Editing the poll since does not change it.
      </p>

      {results.screens.map((screen, index) => (
        <div className="panel" key={screen.screenId}>
          <h2>Screen {index + 1}</h2>
          <ResultBars questions={screen.questions} />
        </div>
      ))}
    </section>
  );
}
